import { formatNumber } from '@rhino-project/core/i18n/format';
import { useTranslation } from '@rhino-project/core/i18n';
import type { ReactNode } from 'react';
import { Link, notFound, useParams } from '@tanstack/react-router';
import { useModelShow } from '@rhino-project/core/hooks';
import { BaseAuthedPage, MaxWidth } from '@rhino-project/ui-heroui';
import { Card, Spinner, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import StarMeter from '../../components/report/StarMeter';
import RatingScore from '../../components/report/RatingScore';
import { summarizeReport } from '../../components/report/ratingSummary';
import type { RhinoRead } from '@rhino-project/core';
import { formatActivityDate } from '../../utils/dates';

type Report = RhinoRead<'report'>;
type ReportValue = Report['report_values'][number];

const MetaPill = ({
  icon,
  children
}: {
  icon: string;
  children: ReactNode;
}) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-secondary px-3 py-1 text-sm text-muted">
    <Icon icon={icon} height={14} width={14} />
    {children}
  </span>
);

const ReportPage = () => {
  const { t } = useTranslation();

  const { owner, id: reportId } = useParams({
    from: '/_authenticated/$owner/reports/$id/'
  });
  const { isLoading, resource: report } = useModelShow('report', reportId!);

  if (isLoading) {
    return <Spinner size="sm" className="mx-auto mt-8 block" />;
  }

  if (!report) throw notFound();

  const r = report;
  const player = r.player;
  const reporter = r.reporter;
  const reportType = r.report_type;
  const reportValues = r.report_values ?? [];

  const fieldType = (rv: ReportValue) => rv.report_field?.field_type;
  const fieldName = (rv: ReportValue) => {
    const f = rv.report_field;
    return f?.display_name ?? f?.name;
  };

  const notes = reportValues.filter(
    (rv) => fieldType(rv) === 'text' && rv.value
  );

  // A single-field "Rating" report is fully expressed by the hero score, so the
  // per-field grade list would just repeat it — summarizeReport only flags
  // showGrades for multi-field reports (Skater/Goalie).
  const summary = summarizeReport(r);

  return (
    <BaseAuthedPage>
      <MaxWidth>
        <div className="flex flex-col gap-4 py-2">
          <Card>
            <Card.Content className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Typography.Heading level={1}>
                    <Link
                      to="/$owner/players/$id"
                      params={{ owner, id: String(player.id) }}
                      className="hover:text-accent"
                    >
                      {player.display_name}
                    </Link>
                  </Typography.Heading>
                  {reportType?.display_name && (
                    <span className="inline-flex items-center rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-accent">
                      {reportType.display_name} {t('frontend.messages.report')}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {r.reported_at && (
                    <MetaPill icon="bi:calendar3">
                      {formatActivityDate(r.reported_at as string)}
                    </MetaPill>
                  )}
                  {r.observing && (
                    <MetaPill icon="bi:eye">
                      <span className="capitalize">
                        {(r.observing as string).replace(/_/g, ' ')}
                      </span>
                    </MetaPill>
                  )}
                  {r.venue && <MetaPill icon="bi:geo-alt">{r.venue}</MetaPill>}
                  {reporter?.display_name && (
                    <MetaPill icon="bi:person">
                      {reporter.display_name}
                    </MetaPill>
                  )}
                </div>
              </div>

              {summary.value != null && (
                <div className="shrink-0 self-start rounded-lg bg-surface-secondary px-6 py-3">
                  <RatingScore
                    value={summary.value}
                    label={summary.label}
                    size="lg"
                  />
                </div>
              )}
            </Card.Content>
          </Card>

          {summary.showGrades && (
            <Card>
              <Card.Content className="flex flex-col">
                {summary.grades.map((g, i) => (
                  <div
                    key={g.id as number}
                    className={`flex items-center justify-between gap-4 py-2.5 ${
                      i > 0 ? 'border-t border-separator' : ''
                    }`}
                  >
                    <span className="text-sm font-medium">{g.name}</span>
                    <div className="flex items-center gap-3">
                      <StarMeter value={g.value} />
                      <span className="w-8 text-right text-sm font-semibold tabular-nums text-muted">
                        {formatNumber(g.value, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </Card.Content>
            </Card>
          )}

          {notes.length > 0 && (
            <Card>
              <Card.Header>
                <Card.Title>{t('frontend.messages.notes')}</Card.Title>
              </Card.Header>
              <Card.Content className="flex flex-col gap-4">
                {notes.map((rv) => (
                  <div key={rv.id}>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
                      {fieldName(rv)}
                    </div>
                    <p className="border-l-2 border-accent/40 pl-3 text-sm leading-relaxed whitespace-pre-line">
                      {rv.value}
                    </p>
                  </div>
                ))}
              </Card.Content>
            </Card>
          )}
        </div>
      </MaxWidth>
    </BaseAuthedPage>
  );
};

export default ReportPage;
